// components/Interactive3DCarousel.tsx
import { useState, useEffect } from 'react';
import { Box, IconButton, useTheme, useMediaQuery, Grid, Paper, Tabs, Tab } from '@mui/material';
import { motion, AnimatePresence, Variants, PanInfo } from 'framer-motion';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// Child Components
import COEResultsCard from './COEResultsCard_Hardcode';
import VehicleTrendHighChart from './VehicleTrendHighChart';
import HomepageTopAd from './HomePageTopAd';
import HomepageBestDealsSection from './HomepageBestDealsSection';

// NEW: Import the AI Chat Section
import AiChatSection from './AiChatSection';

interface Interactive3DCarouselProps {
  adSpaceList: any[];
}

const Interactive3DCarousel = ({ adSpaceList }: Interactive3DCarouselProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // State to toggle between Chart and Best Deals (0 is now Best Deals)
  const [activeMarketTab, setActiveMarketTab] = useState(0);

  // --- CONTENT DEFINITIONS ---

  // 1. Advertisement Content
  const AdContent = (
    <Box sx={{
      width: '95vw',
      maxWidth: '1200px', 
      py: 2,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <HomepageTopAd adSpaceList={adSpaceList} />
    </Box>
  );

  // 2. Market Insights & COE Content
  const MarketContent = (
    <Box
      sx={{
        width: '95vw',
        maxWidth: '1200px', 
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        py: 2
      }}
    >
      <Grid container spacing={2} alignItems="stretch">
        {/* LEFT: Market Insights */}
        <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Paper
            elevation={0}
            sx={{
              flexGrow: 1,
              minHeight: '300px',
              borderRadius: '24px',
              bgcolor: '#fff',
              boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
              p: { xs: 2, md: 3 },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Tabs for switching sections */}
            <Tabs
              value={activeMarketTab}
              onChange={(_, newValue) => setActiveMarketTab(newValue)}
              sx={{
                mb: 2,
                minHeight: '36px',
                '& .MuiTabs-indicator': { backgroundColor: 'primary.main', height: 3, borderRadius: 1.5 },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  minHeight: '36px',
                  py: 0,
                  color: 'text.secondary',
                  '&.Mui-selected': { color: 'text.primary', fontWeight: 800 },
                },
              }}
            >
              <Tab label="Top Picks" />
              <Tab label="Market Trends" />
            </Tabs>

            {/* Conditionally render based on selected tab */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              {activeMarketTab === 0 ? <HomepageBestDealsSection /> : <VehicleTrendHighChart />}
            </Box>
          </Paper>
        </Grid>

        {/* RIGHT: COE Results */}
        <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column' }}>
          <COEResultsCard />
        </Grid>
      </Grid>
    </Box>
  );

  // 3. NEW: AI Chat Assistant Content
  const ChatContent = (
    <Box sx={{
      width: '95vw',
      maxWidth: '1200px', 
      maxHeight: { xs: '80vh', md: '700px' },
      overflow: 'hidden',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '24px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
      bgcolor: theme.palette.background.paper
    }}>
      <AiChatSection />
    </Box>
  );

  // --- CONDITIONAL ITEMS ARRAY ---
  // const rawComponents = (adSpaceList && adSpaceList.length > 0)
  //   ? [AdContent, MarketContent, ChatContent]
  //   : [MarketContent, ChatContent];

    const rawComponents = (adSpaceList && adSpaceList.length > 0)
    ? [AdContent, MarketContent]
    : [MarketContent];

  const items = rawComponents.map((component, index) => ({
    id: index,
    component
  }));

  // --- ANIMATION CONFIG ---
  const CAROUSEL_VARIANTS: Variants = {
    center: {
      x: 0,
      y: 0,
      scale: 1,
      zIndex: 10,
      opacity: 1,
      filter: "blur(0px)",
      rotateY: 0,
      transition: { type: "spring", stiffness: 300, damping: 30 }
    },
    left: {
      x: isMobile ? "-120%" : "-75%",
      y: 0,
      scale: isMobile ? 0.9 : 0.6,
      zIndex: 5,
      opacity: isMobile ? 0 : 0.4,
      filter: "blur(3px)",
      rotateY: isMobile ? 0 : 25,
      transition: { type: "spring", stiffness: 300, damping: 30 }
    },
    right: {
      x: isMobile ? "120%" : "75%",
      y: 0,
      scale: isMobile ? 0.9 : 0.6,
      zIndex: 5,
      opacity: isMobile ? 0 : 0.4,
      filter: "blur(3px)",
      rotateY: isMobile ? 0 : -25,
      transition: { type: "spring", stiffness: 300, damping: 30 }
    },
    hidden: {
      x: 0,
      y: 0,
      scale: 0,
      zIndex: 0,
      opacity: 0
    }
  };

  // --- AUTO ROTATE ---
  useEffect(() => {
    if (isPaused || items.length <= 1) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isPaused, items.length]);

  const getPosition = (index: number) => {
    if (items.length === 1) return "center";

    if (index === activeIndex) return "center";
    const length = items.length;
    const nextIndex = (activeIndex + 1) % length;
    const prevIndex = (activeIndex - 1 + length) % length;
    if (index === nextIndex) return "right";
    if (index === prevIndex) return "left";
    return "hidden";
  };

  const handleDragEnd = (event: any, info: PanInfo) => {
    if (items.length <= 1) return;

    const threshold = 50;
    if (info.offset.x > threshold) {
      setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
    } else if (info.offset.x < -threshold) {
      setActiveIndex((prev) => (prev + 1) % items.length);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: '400px', md: '650px' },
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        perspective: '1200px',
        my: 2,
        pt: { xs: 4, md: 4 },
        pb: { xs: 6, md: 8 }, // Added extra padding at the bottom for the buttons
        overflowX: 'clip',
        overflowY: 'visible',
        backgroundColor: 'transparent',
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
    >

      {/* Carousel Track */}
      <Box sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '1280px', 
        height: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        touchAction: 'pan-y'
      }}>
        <AnimatePresence initial={false} mode='popLayout'>
          {items.map((item, index) => {
            const position = getPosition(index);
            const isCenter = position === 'center';

            return (
              <motion.div
                key={item.id}
                variants={CAROUSEL_VARIANTS}
                initial={position}
                animate={position}
                drag={isMobile && isCenter && items.length > 1 ? "x" : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={handleDragEnd}
                onClick={() => items.length > 1 && setActiveIndex(index)}
                style={{
                  position: isCenter ? 'relative' : 'absolute',
                  cursor: isMobile ? 'grab' : (items.length > 1 ? 'pointer' : 'default'),
                  transformStyle: 'preserve-3d',
                  display: 'flex',
                  justifyContent: 'center',
                  width: isMobile ? '100%' : 'auto',
                  touchAction: 'pan-y',
                  zIndex: position === 'center' ? 10 : 0
                }}
                whileTap={{ cursor: 'grabbing' }}
              >
                <Box sx={{
                  pointerEvents: position === 'center' ? 'all' : 'none',
                  transition: 'opacity 0.3s',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}>
                  {item.component}
                </Box>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </Box>

      {/* --- BOTTOM CONTROLS (Arrows & Dots) --- */}
      {items.length > 1 && (
        <Box sx={{ 
          position: 'absolute', 
          bottom: { xs: -10, md: -20 }, 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 2, md: 3 }, 
          zIndex: 20 
        }}>
          
          {/* Left Arrow */}
          <IconButton
            onClick={() => setActiveIndex((prev) => (prev - 1 + items.length) % items.length)}
            sx={{ 
              bgcolor: '#fff', 
              boxShadow: '0px 4px 14px rgba(0, 0, 0, 0.1)', 
              width: '45px',
              height: '45px',
              '&:hover': { bgcolor: '#fafafa', transform: 'scale(1.05)' },
              transition: 'all 0.2s ease'
            }}
          >
            <ArrowBackIosNewIcon sx={{ color: '#2C3E50', fontSize: '1.2rem', ml: 0.5 }} />
          </IconButton>

          {/* Pagination Dots */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {items.map((_, i) => (
              <Box
                key={i}
                onClick={() => setActiveIndex(i)}
                sx={{
                  width: activeIndex === i ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: activeIndex === i ? 'linear-gradient(135deg, #FFA100 0%, #FF8E00 100%)' : 'rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  boxShadow: activeIndex === i ? '0px 2px 8px rgba(255, 142, 0, 0.4)' : 'none'
                }}
              />
            ))}
          </Box>

          {/* Right Arrow */}
          <IconButton
            onClick={() => setActiveIndex((prev) => (prev + 1) % items.length)}
            sx={{ 
              bgcolor: '#fff', 
              boxShadow: '0px 4px 14px rgba(0, 0, 0, 0.1)', 
              width: '45px',
              height: '45px',
              '&:hover': { bgcolor: '#fafafa', transform: 'scale(1.05)' },
              transition: 'all 0.2s ease'
            }}
          >
            <ArrowForwardIosIcon sx={{ color: '#2C3E50', fontSize: '1.2rem' }} />
          </IconButton>
          
        </Box>
      )}
    </Box>
  );
};

export default Interactive3DCarousel;